import React, { useState } from 'react';
import BackButton from '../components/BackButton';

// Design tokens — "Product B2B Page" (sticky-note board look)
const PAGE_BG = '#FDF5E6';
const INK = '#111';
const INK_80 = 'rgba(0,0,0,.8)';
const INK_62 = 'rgba(0,0,0,.62)';
const INK_55 = 'rgba(0,0,0,.55)';
const INK_50 = 'rgba(0,0,0,.5)';
const INK_45 = 'rgba(0,0,0,.45)';
const INK_40 = 'rgba(0,0,0,.4)';
const INK_35 = 'rgba(0,0,0,.35)';
const INK_18 = 'rgba(0,0,0,.18)';
const INK_12 = 'rgba(0,0,0,.12)';
const INK_10 = 'rgba(0,0,0,.1)';

const FONT_DISPLAY = "'Bayon', sans-serif";
const FONT_BODY = "'K2D', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const NOTE_ANGLES = [-1.6, 1.3, -0.9];

const CASE_STUDIES = [
  {
    key: 'ercc',
    org: 'Bethink Labs',
    date: 'Oct 2025',
    role: 'Senior PM',
    image: '/ERCCImage.png',
    title: 'Emergency Call Center Platform Migration',
    metric: '$M+',
    metricStamp: 'ANNUAL SAVINGS',
    metricLabel: 'saved per year in platform costs',
    accent: 'linear-gradient(135deg,#84FAB0 0%,#8FD3F4 100%)',
    flat: '#B9F3D6',
    stats: [
      { value: '24/7', label: 'emergency operations supported' },
      { value: 'In-house', label: 'platform fully owned, no third party' },
      { value: 'Real-time', label: 'vehicle location and remote control' },
    ],
    content:
      "I led the migration of an automobile company's Emergency Response Call Center from a legacy third-party provider to an internally controlled platform as part of a company-wide cost and reliability transformation. The initiative replaced safety-critical backend capabilities used during accidents, roadside emergencies, and stolen vehicle incidents, significantly improving API reliability, response times, and development velocity while enabling millions of dollars in annual cost savings. The new platform supports 24/7 emergency operations, real-time vehicle location and control, and compliance with regional regulatory requirements, while establishing a scalable foundation for future reporting and safety feature expansion.",
  },
  {
    key: 'ml',
    org: 'Shakudo',
    date: 'Dec 2022',
    role: 'Product Manager',
    image: '/MLImage.png',
    title: 'Launching ML Platform to Streamline DevOps',
    metric: '3×',
    metricStamp: 'UPSELL REVENUE',
    metricLabel: 'increase in upsell revenue after launch',
    accent: 'linear-gradient(135deg,#C471F5 0%,#FA71CD 100%)',
    flat: '#F3C9EE',
    stats: [
      { value: '3×', label: 'increase in upsell revenue' },
      { value: 'Zero', label: 'DevOps tickets to reach production' },
      { value: 'Self-serve', label: 'production provisioning in-platform' },
    ],
    content:
      'Previously, data scientists relied on DevOps and engineering teams to convert their pipelines into production-ready deployments. To streamline the path from development to production, we built a feature that enabled DevOps to provision production environments directly within the platform. This eliminated deployment bottlenecks, significantly reduced wait times, and ultimately contributed to a 3× increase in upsell revenue.',
  },
  {
    key: 'cs',
    org: 'Kira Talent',
    date: 'Jun 2021',
    role: 'Associate PM',
    image: '/CSImage.png',
    title: 'CRM Tool for Customer Success Teams',
    metric: '2h → 5m',
    metricStamp: 'TIME TO INSIGHT',
    metricLabel: 'to generate client-level insights',
    accent: 'linear-gradient(135deg,#F6D365 0%,#FDA085 100%)',
    flat: '#FBE0AE',
    stats: [
      { value: '2h → 5m', label: 'time to client-level insight' },
      { value: 'Zero', label: 'engineering hours per report' },
      { value: 'Cross-team', label: 'built with devs and CS together' },
    ],
    content:
      'Customer Success teams previously had no direct access to customer usage data and had to rely on engineering support for any quantitative reporting. To remove this dependency and free up engineering time, I led an initiative to implement a business intelligence tool in close collaboration with developers and the Customer Success team. As a result, the time required to generate client-level insights was reduced from over two hours to under five minutes.',
  },
];

const ProductB2BPage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [readIndices, setReadIndices] = useState(new Set([0]));
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const pick = (i) => {
    setActiveIndex(i);
    setReadIndices((prev) => (prev.has(i) ? prev : new Set(prev).add(i)));
  };

  const active = CASE_STUDIES[activeIndex];

  return (
    <div
      className="min-h-screen w-full"
      style={{
        backgroundColor: PAGE_BG,
        backgroundImage:
          'linear-gradient(rgba(0,0,0,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.045) 1px,transparent 1px)',
        backgroundSize: '28px 28px',
        fontFamily: FONT_BODY,
      }}
    >
      <style>{`
        @keyframes b2bRiseIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div
        className="mx-auto flex flex-col"
        style={{ maxWidth: '1320px', padding: '26px 20px 40px', gap: '26px', minHeight: '100vh', boxSizing: 'border-box' }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3">
          <BackButton color={INK_55} hoverColor="#000" fontFamily={FONT_BODY} />
          <div
            className="text-right"
            style={{ font: `500 10.5px ${FONT_MONO}`, letterSpacing: '.16em', color: INK_40 }}
          >
            PRODUCT / B2B · {CASE_STUDIES.length} CASE STUDIES
          </div>
        </div>

        {/* Header */}
        <div className="flex items-end justify-between gap-10 flex-wrap">
          <div
            style={{
              font: `400 clamp(40px,6.4vw,88px)/.9 ${FONT_DISPLAY}`,
              color: INK,
              letterSpacing: '.005em',
            }}
          >
            B2B / WHAT I SHIPPED
          </div>
          <div style={{ font: `400 13px/1.6 ${FONT_BODY}`, color: INK_55, maxWidth: '330px', textWrap: 'pretty' }}>
            Enterprise platforms and internal tools. Tap a note to open the story — the number in the corner is the
            outcome.
          </div>
        </div>

        {/* Board */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-stretch flex-1 md:min-h-0">
          {/* Notes */}
          <div className="w-full md:w-[400px] md:flex-none flex flex-col gap-5 md:pt-1.5">
            {CASE_STUDIES.map((c, i) => {
              const isActive = i === activeIndex;
              const isRead = readIndices.has(i);
              const isHovered = hoveredIndex === i;
              const readLabel = isActive ? 'OPEN' : isRead ? 'READ' : 'NEW';
              const badgeStyle = {
                font: `700 8.5px ${FONT_MONO}`,
                letterSpacing: '.12em',
                padding: '3px 7px',
                borderRadius: '3px',
                ...(isActive
                  ? { background: INK, color: '#fff' }
                  : isRead
                  ? { color: INK_40, border: `1px solid ${INK_18}` }
                  : { background: INK, color: '#fff', opacity: 0.75 }),
              };
              const transform = isHovered
                ? 'rotate(0deg) scale(1.025)'
                : `rotate(${NOTE_ANGLES[i]}deg)${isActive ? ' scale(1.03)' : ''}`;

              return (
                <div
                  key={c.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => pick(i)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick(i)}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    cursor: 'pointer',
                    background: c.flat,
                    padding: '20px 20px 16px',
                    borderRadius: '2px',
                    boxShadow: isActive ? '6px 7px 0 rgba(0,0,0,.17)' : '3px 4px 0 rgba(0,0,0,.09)',
                    transform,
                    opacity: isActive ? 1 : 0.66,
                    transition: 'transform .22s ease, opacity .22s ease, box-shadow .22s ease',
                    outline: isActive ? `2px solid ${INK}` : 'none',
                    outlineOffset: '-2px',
                  }}
                >
                  <div className="flex justify-between items-start gap-3.5">
                    <div style={{ font: `400 28px/1.02 ${FONT_DISPLAY}`, color: INK, maxWidth: '215px' }}>
                      {c.title}
                    </div>
                    <div className="text-right flex-none">
                      <div style={{ font: `400 34px/1 ${FONT_DISPLAY}`, color: INK }}>{c.metric}</div>
                      <div
                        style={{
                          font: `500 8.5px ${FONT_MONO}`,
                          letterSpacing: '.1em',
                          color: INK_50,
                          marginTop: '2px',
                        }}
                      >
                        {c.metricStamp}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between" style={{ marginTop: '16px' }}>
                    <span style={{ font: `500 10px ${FONT_MONO}`, color: INK_50 }}>
                      {c.org} · {c.date}
                    </span>
                    <span style={badgeStyle}>{readLabel}</span>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 'auto', font: `400 11px/1.5 ${FONT_BODY}`, color: INK_35, paddingTop: '20px' }}>
              Scroll the panel for the full write-up.
            </div>
          </div>

          {/* Detail panel */}
          <div
            key={active.key}
            className="flex-1 min-w-0 flex flex-col overflow-hidden"
            style={{
              background: '#fff',
              border: `1px solid ${INK_12}`,
              borderRadius: '10px',
              boxShadow: '0 3px 0 rgba(0,0,0,.06)',
              animation: 'b2bRiseIn .34s ease both',
            }}
          >
            <div style={{ height: '9px', background: active.accent, flex: 'none' }} />
            <div
              className="flex flex-col overflow-auto flex-1 min-h-0"
              style={{ padding: '28px 22px 30px', gap: '20px' }}
            >
              <div className="flex items-center gap-2.5">
                <span
                  style={{ width: 13, height: 13, borderRadius: '50%', background: active.accent, flexShrink: 0 }}
                />
                <span style={{ font: `500 10.5px ${FONT_MONO}`, letterSpacing: '.12em', color: INK_45 }}>
                  {active.org} · {active.date} · {active.role}
                </span>
              </div>
              <div style={{ font: `400 clamp(30px,3.4vw,48px)/1.02 ${FONT_DISPLAY}`, color: INK, maxWidth: '720px' }}>
                {active.title}
              </div>
              <div className="flex gap-6 items-start flex-wrap">
                <div style={{ flex: 'none', padding: '2px 0 2px 18px', borderLeft: `3px solid ${INK}` }}>
                  <div style={{ font: `400 60px/.9 ${FONT_DISPLAY}`, color: INK }}>{active.metric}</div>
                  <div style={{ font: `400 12px/1.35 ${FONT_BODY}`, color: INK_62, marginTop: '6px', maxWidth: '190px' }}>
                    {active.metricLabel}
                  </div>
                </div>
                <div
                  className="flex-1"
                  style={{
                    minWidth: '240px',
                    border: `1px solid ${INK_12}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    height: '172px',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url("${active.image}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'top center',
                    }}
                  />
                </div>
              </div>
              <p style={{ margin: 0, font: `400 15px/1.75 ${FONT_BODY}`, color: INK_80, maxWidth: '760px', textWrap: 'pretty' }}>
                {active.content}
              </p>
              <div
                className="flex gap-8 flex-wrap"
                style={{ marginTop: 'auto', paddingTop: '22px', borderTop: `1px solid ${INK_10}` }}
              >
                {active.stats.map((s) => (
                  <div key={s.label}>
                    <div style={{ font: `400 32px/1 ${FONT_DISPLAY}`, color: INK }}>{s.value}</div>
                    <div style={{ font: `400 11px/1.35 ${FONT_BODY}`, color: INK_50, maxWidth: '170px', marginTop: '4px' }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductB2BPage;
